/**
 * Scope chooser - the reusable blast-radius dropdown for "Approve Always"
 * (spec §6). Given the applicable scope rungs (narrow→broad, from the backend
 * `enumerate_scope_rungs`), it renders a Web Awesome dropdown of human-readable levels:
 *
 *   - The NARROWEST selectable rung is chosen by default.
 *   - Never-auto rungs (outside / new / flagged contacts) render DISABLED with
 *     their plain reason shown beneath - a teachable moment, never hidden
 *     (CONTEXT.md "Scope level").
 *   - Confirm emits the chosen `scope_tier`; Cancel emits nothing.
 *
 * Reusable by design: the toast-host (slice 03's "Approve Always") and the
 * Approvals-page queue card use it now; the editable auto-approvals list
 * (slice 07's change-scope) reuses it next. The host owns what to DO with the
 * chosen scope (promote vs change) - this control only picks one.
 */

import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, output, signal } from '@angular/core';

import { ScopeRung } from '../../services/ddr.service';

@Component({
    selector: 'app-scope-chooser',
    templateUrl: './scope-chooser.html',
    styleUrl: './scope-chooser.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScopeChooser {
    /** Applicable rungs, narrow→broad (backend order preserved). */
    readonly rungs = input<ScopeRung[]>([]);
    /** Disable the confirm/select while a promotion is in flight. */
    readonly busy = input<boolean>(false);
    /** Confirm-button label (e.g. "Approve Always" / "Change scope"). */
    readonly confirmLabel = input<string>('Approve Always');

    /** Emits the chosen scope_tier on confirm. */
    readonly confirmed = output<string>();
    /** Emits on cancel/dismiss. */
    readonly cancelled = output<void>();

    /** The selectable (unlocked) rungs - the narrowest is the default pick. */
    readonly selectableRungs = computed(() => this.rungs().filter(r => !r.locked));

    /** Currently-selected scope_tier. Defaults to the narrowest selectable rung
     *  (the first unlocked entry, since rungs arrive narrow→broad). Null when
     *  nothing is selectable (every applicable rung is locked). */
    readonly selected = signal<string | null>(null);

    /** Resolve the effective selection: the explicit pick, else the narrowest
     *  selectable rung. Returns '' when nothing is selectable so `onConfirm`
     *  emits nothing. */
    readonly effectiveSelected = computed((): string => {
        const explicit = this.selected();
        if (explicit && this.selectableRungs().some(r => r.scope_tier === explicit)) {
            return explicit;
        }
        return this.selectableRungs()[0]?.scope_tier ?? '';
    });

    /** True when no rung can be auto-approved - confirm is disabled. */
    readonly allLocked = computed(() => this.selectableRungs().length === 0);

    readonly selectedLabel = computed(() => {
        const selected = this.effectiveSelected();
        return this.selectableRungs().find(r => r.scope_tier === selected)?.label ?? 'No auto-approval scopes';
    });

    onSelect(value: string): void {
        this.selected.set(value || null);
    }

    onDropdownSelect(event: Event): void {
        const item = (event as CustomEvent<{ item?: { value?: string } }>).detail?.item;
        this.onSelect(item?.value ?? '');
    }

    onConfirm(): void {
        const tier = this.effectiveSelected();
        if (!tier || this.busy()) {
            return;
        }
        this.confirmed.emit(tier);
    }

    onCancel(): void {
        this.cancelled.emit();
    }
}
